// printer.js
// Shared Epson ePOS SDK helpers. Consolidates ticket layout and the connect/send
// dance that's shared between Register (Flash/Custom/Magic/Promo tickets) and
// Campaigns (golden-ticket-format marketing QRs).
//
// Usage:
//   import { printJobs } from "./utils/printer";
//   await printJobs(jobs, { onStatus: ({ pct, msg }) => ... });
//
// Jobs are plain objects; buildPrintBuffer below interprets job.ticketType to
// pick layout. Campaigns reuse ticketType:"promo" since the layout is the
// same golden-ticket-style QR ticket.

const DEFAULT_PRINTER_IP   = "192.168.1.69";
const DEFAULT_PRINTER_PORT = "80";
const EPOS_SDK_SRC         = "/epos-2.27.0.js";

// Idempotent SDK load. Safe to call multiple times.
async function loadEposSDK() {
  if (window.epson) return;
  await new Promise((resolve, reject) => {
    // Guard against duplicate script tags if called in parallel.
    const existing = document.querySelector(`script[src="${EPOS_SDK_SRC}"]`);
    if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", () => reject(new Error("Failed to load ePOS SDK"))); return; }
    const script = document.createElement("script");
    script.src = EPOS_SDK_SRC;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load ePOS SDK"));
    document.head.appendChild(script);
  });
}

// Connect to the printer, build all tickets in the batch, send as one job.
// onStatus fires at each phase boundary with { pct, msg } so UIs can show progress.
export async function printJobs(jobs, options = {}) {
  const {
    onStatus  = () => {},
    printerIp = DEFAULT_PRINTER_IP,
    printerPort = DEFAULT_PRINTER_PORT,
    // Covers connect + device open only — never the print itself, so a long
    // campaign batch can't false-alarm mid-print. 0 disables.
    connectTimeoutMs = 20000,
  } = options;

  await loadEposSDK();

  return new Promise((resolve, reject) => {
    const epos = new window.epson.ePOSDevice();
    let settled = false;
    const fail = (err) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      try { epos.disconnect(); } catch { /* already down */ }
      reject(err);
    };
    // The SDK's connect callback simply never fires when the printer is
    // unreachable (wrong network, asleep) — without this the promise hangs forever.
    const timer = connectTimeoutMs
      ? setTimeout(() => fail(new Error("Printer unreachable — check this device is on the printer's network, then tap again.")), connectTimeoutMs)
      : null;

    onStatus({ pct: 20, msg: "Connecting to printer..." });
    epos.connect(printerIp, printerPort, (connectResult) => {
      if (settled) return;
      if (connectResult !== "OK" && connectResult !== "SSL_CONNECT_OK") {
        fail(new Error(`Printer's napping — tap again. 👋 (${connectResult})`));
        return;
      }

      onStatus({ pct: 45, msg: "Opening printer..." });
      epos.createDevice(
        "local_printer",
        epos.DEVICE_TYPE_PRINTER,
        { crypto: false, buffer: false },
        (printer, code) => {
          if (settled) return;
          if (timer) clearTimeout(timer);
          if (code !== "OK") {
            fail(new Error(`Printer device error: ${code}`));
            return;
          }

          const n = jobs.length;
          onStatus({ pct: 65, msg: `Building ${n} ticket${n > 1 ? "s" : ""}...` });

          for (const job of jobs) {
            buildPrintBuffer(printer, job);
          }

          onStatus({ pct: 85, msg: "Sending to printer..." });

          printer.onreceive = () => {
            onStatus({ pct: 100, msg: `Done! 🎟 ${n > 1 ? "Tear 'em off!" : "Tear it off!"}` });
            epos.deleteDevice(printer, () => {
              epos.disconnect();
              if (!settled) { settled = true; resolve(); }
            });
          };

          printer.onerror = (err) => {
            epos.deleteDevice(printer, () => {
              fail(new Error(`Printer error: ${err.status ?? JSON.stringify(err)}`));
            });
          };

          printer.send();
        }
      );
    });
  });
}

// ── PRINT BUFFER ───────────────────────────────────────────────────────────
// Builds one ticket's print commands onto the printer object.
// All tickets for a batch are built in sequence before a single send().
// Each ticket ends with addCut() — auto-cuts between tickets.

// Separators print at matching width (~32 cols) so Magic/promo tickets
// don't read narrower than Flash/Custom.
const STAR_SEP = "* ~ * ~ * ~ * ~ * ~ * ~ * ~ * ~ *\n";
const DASH_SEP = "--------------------------------\n";

export function buildPrintBuffer(printer, job) {
  const IS_CUSTOM  = job.ticketType === "custom";
  const IS_MAGIC   = job.ticketType === "magic";
  const IS_PROMO   = job.ticketType === "promo";
  const IS_REISSUE = job.ticketType === "reissue";
  // Register's "Signature line" toggle. Only an explicit false omits the
  // line — jobs that don't pass it (campaigns, reissue) keep it.
  const SHOW_SIG  = job.showSignature !== false;

  printer.addTextAlign(printer.ALIGN_CENTER);
  printer.addTextSmooth(true); // anti-alias the scaled (2x2 / 1x2) text

  // ── PROMO TICKET — special layout for secret/guerilla tickets ──
  if (IS_PROMO) {
    printer.addText("\n");
    printer.addTextSize(2, 2);
    printer.addTextStyle(true, false, true, printer.COLOR_1);
    printer.addText("  GOLDEN TICKET  \n");
    printer.addTextStyle(false, false, false, printer.COLOR_1);
    printer.addTextSize(1, 1);
    printer.addText("The Joy of Pancakes\n");
    // Skip "admit one" when campaign tagline already frames the ticket
    if (!job.tagline) {
      printer.addText("~ admit one ~\n");
    }
    printer.addText(STAR_SEP);
    // Serial number
    if (job.serial) {
      printer.addTextStyle(false, false, true, printer.COLOR_1);
      printer.addText(`${job.serial}\n`);
      printer.addTextStyle(false, false, false, printer.COLOR_1);
    }
    // Expiry display text
    if (job.expiryDisplay) {
      printer.addText("\n");
      printer.addTextSize(1, 2);
      printer.addTextStyle(true, false, true, printer.COLOR_1);
      printer.addText(`  ${job.expiryDisplay}  \n`);
      printer.addTextStyle(false, false, false, printer.COLOR_1);
      printer.addTextSize(1, 1);
    }
    printer.addText(STAR_SEP);
    if (job.tagline) {
      printer.addText(`${job.tagline}\n`);
    }
    // Campaigns pass a per-campaign subtitle ("Scan to reveal your pancake" /
    // "Scan to learn more"); fall back to the generic prompt.
    printer.addText(`${job.subtitle || "Scan to begin >>"}\n`);
    printer.addText("\n");
    printer.addSymbol(job.orderUrl, printer.SYMBOL_QRCODE_MODEL_2, printer.LEVEL_Q, 6, 0, 0);
    printer.addText("\n");
    printer.addText(STAR_SEP);
    printer.addText("\n");
    if (SHOW_SIG) {
      printer.addText("x_______________________\n");
      printer.addText("\n");
    }
    if (job.hasPancake !== false) {
      printer.addText("Your pancake lives here.\n");
      printer.addText("Scan anytime, forever.\n");
    } else {
      // Campaign with no linked pancake — don't promise one.
      printer.addText("Made with joy in St. Louis.\n");
      printer.addText("See you at the griddle.\n");
    }
    printer.addText("\n");
    printer.addCut(printer.CUT_FEED);
    return;
  }

  // ── RE-ISSUED TICKET — museum-placard layout ──
  // Prints with preserved/framed pancakes as a gallery label, but the copy
  // stays universal enough to hand out as an in-person ticket replacement.
  if (IS_REISSUE) {
    printer.addText("\n");
    printer.addTextSize(2, 2);
    printer.addTextStyle(false, false, true, printer.COLOR_1);
    printer.addText("GOLDEN TICKET\n");
    printer.addTextStyle(false, false, false, printer.COLOR_1);
    printer.addTextSize(1, 1);
    printer.addText("The Joy of Pancakes\n");
    printer.addText("\n");
    printer.addText("~ * ~\n");
    printer.addText("\n");
    // Placard title block: piece title (when the order has one) over the
    // medium line, museum-style.
    if (job.title) {
      printer.addTextSize(1, 2);
      printer.addTextStyle(false, false, true, printer.COLOR_1);
      printer.addText(`"${job.title}"\n`);
      printer.addTextStyle(false, false, false, printer.COLOR_1);
      printer.addTextSize(1, 1);
    }
    printer.addText(job.year ? `Batter on griddle, ${job.year}\n` : "Batter on griddle\n");
    printer.addText("\n");
    printer.addText("~ * ~\n");
    printer.addText("\n");
    printer.addSymbol(job.orderUrl, printer.SYMBOL_QRCODE_MODEL_2, printer.LEVEL_Q, 6, 0, 0);
    printer.addText("\n");
    printer.addText("Your pancake lives here.\n");
    printer.addText("Scan anytime, forever.\n");
    printer.addText("\n");
    printer.addText("\n");
    if (SHOW_SIG) {
      printer.addText("x_______________________\n");
      printer.addText("\n");
    }
    printer.addCut(printer.CUT_FEED);
    return;
  }

  // GOLDEN TICKET header
  // Custom + Magic: reverse (white text on black bar) for at-a-glance distinction
  // Flash: standard bold
  printer.addTextSize(2, 2);
  printer.addTextStyle(IS_CUSTOM || IS_MAGIC, false, true, printer.COLOR_1);
  // Reversed headers get padding so the black bar doesn't hug the glyphs
  printer.addText(IS_CUSTOM || IS_MAGIC ? "  GOLDEN TICKET  \n" : "GOLDEN TICKET\n");
  printer.addTextStyle(false, false, false, printer.COLOR_1);

  printer.addTextSize(1, 1);
  printer.addText("The Joy of Pancakes\n");
  printer.addText("~ admit one ~\n");

  if (IS_MAGIC) {
    printer.addText(STAR_SEP);
  } else {
    printer.addText(DASH_SEP);
  }

  // Ticket type label
  printer.addTextSize(1, 2);
  if (IS_CUSTOM) {
    printer.addTextStyle(true, false, true, printer.COLOR_1);
    printer.addText("  * CUSTOM *  \n");
    printer.addTextStyle(false, false, false, printer.COLOR_1);
  } else if (IS_MAGIC) {
    printer.addTextStyle(true, false, true, printer.COLOR_1);
    printer.addText(" * -*- MAGIC -*- * \n");
    printer.addTextStyle(false, false, false, printer.COLOR_1);
  } else {
    printer.addTextStyle(false, false, true, printer.COLOR_1);
    printer.addText("FLASH\n");
    printer.addTextStyle(false, false, false, printer.COLOR_1);
  }

  // Expiry — prominent, same height as ticket type
  printer.addTextStyle(false, false, true, printer.COLOR_1);
  printer.addText(`${job.expiryDisplay}\n`);
  printer.addTextStyle(false, false, false, printer.COLOR_1);
  printer.addTextSize(1, 1);

  if (IS_MAGIC) {
    printer.addText(STAR_SEP);
  } else {
    printer.addText(DASH_SEP);
  }
  printer.addText("Scan to begin >>\n");

  printer.addText("\n");
  // LEVEL_Q (~25% damage tolerance): tickets live folded in pockets on
  // thermal stock that scuffs and fades — cheap insurance for "scan forever".
  printer.addSymbol(job.orderUrl, printer.SYMBOL_QRCODE_MODEL_2, printer.LEVEL_Q, 6, 0, 0);
  printer.addText("\n");

  if (IS_MAGIC) {
    printer.addText(STAR_SEP);
  } else {
    printer.addText(DASH_SEP);
  }

  printer.addText("\n");
  if (SHOW_SIG) {
    printer.addText("x_______________________\n");
    printer.addText("\n");
  }

  if (IS_MAGIC) {
    printer.addText("A gift from us.\n");
    printer.addText("Yours forever.\n");
  } else {
    printer.addText("Your pancake lives here.\n");
    printer.addText("Scan anytime, forever.\n");
  }

  printer.addText("\n");
  printer.addCut(printer.CUT_FEED);
}
